import { useMemo } from 'react'
import { useIntl } from 'react-intl'
import { FilterDropdown, FilterDropdownItemProps } from '@carto/meridian-ds/components'
import { FilterAltOutlined } from '@mui/icons-material'

import { MapOwnerFilter } from 'features/maps/types'
import { ContextMenuItem } from 'features/common/ui/ContextMenu'
import { UserRole } from 'features/users'
import useUserRoles from 'features/account/state/useUserRoles'
import useIsIndividualUser from 'features/users/state/useIsIndividualUser'

interface MapsFilterOwnerSelector {
  value?: MapOwnerFilter
  onFilterChange: (filter: MapOwnerFilter) => void
}

function MapsFilterOwnerSelector({ value, onFilterChange }: MapsFilterOwnerSelector) {
  const intl = useIntl()
  const { shouldShowToUser } = useUserRoles()
  const isIndividualUser = useIsIndividualUser()

  // Note: If UI needs to change, this component could be split in 2, a MapsFilterByOwner and a MapsFilterByPrivacy
  const filterItems = useMemo(
    () => [
      {
        id: MapOwnerFilter.All,
        label: intl.formatMessage({ id: 'maps.filters.privacy.all' })
      },
      {
        id: MapOwnerFilter.Mine,
        label: intl.formatMessage({ id: 'maps.filters.privacy.myMaps' }),
        hidden: !shouldShowToUser([UserRole.Viewer])
      },
      {
        id: MapOwnerFilter.Shared,
        label: intl.formatMessage({ id: 'maps.filters.privacy.shared' }),
        hidden: isIndividualUser
      },
      {
        id: MapOwnerFilter.Public,
        label: intl.formatMessage({ id: 'maps.filters.privacy.public' })
      }
    ],
    [intl, isIndividualUser, shouldShowToUser]
  )

  const getItemFromMapFilter = (filter = MapOwnerFilter.All): FilterDropdownItemProps => {
    const item = filterItems.find((item) => item.id === filter)
    if (!item) throw TypeError()

    return item
  }

  const handleItemClick = (selectedItem: ContextMenuItem) => {
    onFilterChange(selectedItem.id as MapOwnerFilter)
  }

  return (
    <FilterDropdown
      icon={<FilterAltOutlined />}
      menuItems={filterItems}
      onClickItem={handleItemClick}
      value={getItemFromMapFilter(value)}
      responsive
    />
  )
}

export default MapsFilterOwnerSelector
